import { isDefined } from '@priva/utilities/predicate';

import { ProductBoxRegistration } from '../components/product-box/models';
import { Activity } from '../features/activity-selector/models';
import { ResultDeterminationType } from '../models';
import { validateAmountResultFormat } from './amount-result.utils';
import { arrayEqual } from './array.utils';

const compareProductBoxRegistrations = (a: ProductBoxRegistration, b: ProductBoxRegistration) =>
    a.productBoxNumber - b.productBoxNumber;

export function sortProductBoxRegistrations(list: ProductBoxRegistration[]) {
    return [...list].sort(compareProductBoxRegistrations);
}

export function resetProductBoxRegistrationAmounts(
    productBoxRegistrations: ProductBoxRegistration[],
    isAmountRequired: boolean,
) {
    return productBoxRegistrations.map(
        ({ productBoxNumber }): ProductBoxRegistration => ({
            amount: isAmountRequired ? undefined : null,
            productBoxNumber,
        }),
    );
}

/*
    Compares two arrays of ProductBoxRegistration for equality, ignoring the order of elements.
    Uses a custom comparison function to compare individual ProductBoxRegistration objects.
 */
export function areProductBoxRegistrationArraysEqual(
    a: ProductBoxRegistration[],
    b: ProductBoxRegistration[],
) {
    return arrayEqual(a, b, compareProductBoxRegistrations);
}

/* Validation differs by ResultDeterminationType
    byAmountAndProductBox requires at least one amount to be more than or equals to 0 (not null/undefined)
    ByContainerAndProductBox requires all amount to be null
 */
function areProductBoxRegistrationsAmountValid(
    activity: Activity | undefined,
    productBoxRegistrations: ProductBoxRegistration[],
    resultDeterminationType?: ResultDeterminationType,
) {
    return resultDeterminationType === ResultDeterminationType.ByContainerAndProductBox
        ? productBoxRegistrations.every(({ amount }) => amount === null)
        : productBoxRegistrations.some(
              ({ productBoxNumber, amount }) =>
                  productBoxNumber !== undefined &&
                  isDefined(amount) &&
                  amount >= 0 &&
                  validateAmountResultFormat({ amountResult: amount, activity }),
          );
}

export function areProductBoxRegistrationsValid(
    productBoxRegistrations: ProductBoxRegistration[],
    activity: Activity | undefined,
) {
    return (
        productBoxRegistrations.length > 0 &&
        areProductBoxRegistrationsAmountValid(
            activity,
            productBoxRegistrations,
            activity?.resultDeterminationType,
        )
    );
}

const withAmount = ({ amount, productBoxNumber }: ProductBoxRegistration) =>
    (amount || amount === 0) && productBoxNumber;

export function filterProductBoxRegistrationsWithAmount(productBoxRegistrations: ProductBoxRegistration[]) {
    return productBoxRegistrations.filter(withAmount);
}
